import { useEffect } from 'react'
import { RoomEvent, Room, DataPacket_Kind, RemoteParticipant } from 'livekit-client'

import { clientLogInfo, clientLogWarn } from '@/src/lib/clientLog'

/**
 * Команда старого впуска по DataChannel. Новые клики её больше не шлют.
 *
 * Хук остаётся, чтобы увидеть запоздалый пакет с другой вкладки или
 * со старого клиента и не запускать из него перезаход в комнату.
 */
interface ReconnectionCommand {
  action: string
  targetParticipant: string
  roomName: string
  participantName: string
  timestamp: number
  questionSessionId?: string
}

interface UseReconnectionHandlerOptions {
  room: Room
  identity: string
}

/**
 * Слушает устаревшие `admin_commands` и только пишет лог, без перезахода.
 *
 * Впуск теперь делает сервер через `UpdateParticipant`. Если пакет всё же
 * долетит, гость уже обрабатывает смену прав в `ActiveRoom`, и повторный
 * `disconnect`/`connect` только сломает сессию.
 */
export function useReconnectionHandler({ room, identity }: UseReconnectionHandlerOptions) {
  useEffect(() => {
    if (!room) return

    /**
     * Логирует запоздалую команду впуска и не запускает перезаход.
     */
    const handleDataReceived = (
      payload: Uint8Array,
      _participant?: RemoteParticipant,
      _kind?: DataPacket_Kind,
      topic?: string,
    ) => {
      if (topic !== 'admin_commands') return

      try {
        const decoder = new TextDecoder('utf-8')
        const command = JSON.parse(decoder.decode(payload)) as ReconnectionCommand
        const isMatch =
          command.action === 'reconnect_with_elevated_permissions' &&
          command.targetParticipant === identity

        clientLogInfo('waiting-room-admit received leftover admin_commands', {
          room: command.roomName,
          targetIdentity: command.targetParticipant,
          localIdentity: identity,
          action: command.action,
          match: isMatch,
          questionSessionId: command.questionSessionId,
        })
      } catch (e) {
        clientLogWarn('waiting-room-admit failed to parse admin_commands', {
          localIdentity: identity,
          error: e instanceof Error ? e.message : String(e),
        })
      }
    }

    room.on(RoomEvent.DataReceived, handleDataReceived)

    return () => {
      room.off(RoomEvent.DataReceived, handleDataReceived)
    }
  }, [room, identity])
}
